// Pesos de la DIAN para el dígito de verificación, desde el último dígito hacia el primero
const DIAN_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

export const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * Dígito de verificación de un NIT según la DIAN
 * @param {string} nit número del NIT sin dígito de verificación
 * @returns {number|null} null si el NIT todavía no tiene suficientes dígitos
 */
export const nitCheckDigit = (nit) => {
  const digits = onlyDigits(nit);
  if (digits.length < 6) return null;
  const total = digits.split('').reverse()
    .reduce((sum, digit, index) => sum + Number(digit) * DIAN_WEIGHTS[index], 0);
  const residue = total % 11;
  return residue > 1 ? 11 - residue : residue;
};

/**
 * Deja solo lo que admite cada tipo de documento: cédula y NIT solo números, sin guion ni dígito
 * de verificación; pasaporte y cédula de extranjería letras y números
 */
export const sanitizeDocumentNumber = (documentType, value) => {
  if (['PA', 'CE'].includes(documentType)) return String(value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return onlyDigits(value);
};

const LIMITS = {
  CC: { min: 5, max: 10, text: 'La cédula debe tener entre 5 y 10 dígitos' },
  NIT: { min: 6, max: 10, text: 'El NIT debe tener entre 6 y 10 dígitos, sin el dígito de verificación' },
  CE: { min: 3, max: 15, text: 'El documento debe tener entre 3 y 15 caracteres' },
  PA: { min: 3, max: 15, text: 'El pasaporte debe tener entre 3 y 15 caracteres' },
};

/**
 * @returns {string|null} el error del número de documento, o null si está bien
 */
export const documentNumberError = (documentType, value) => {
  const limits = LIMITS[documentType];
  const clean = sanitizeDocumentNumber(documentType, value);
  if (!limits || !clean) return null;
  return clean.length < limits.min || clean.length > limits.max ? limits.text : null;
};

/**
 * @returns {string|null} el error del dígito de verificación, o null si corresponde al NIT
 */
export const nitCheckDigitError = (nit, checkDigit) => {
  const expected = nitCheckDigit(nit);
  if (expected === null || checkDigit === '' || checkDigit === null || checkDigit === undefined) return null;
  return Number(checkDigit) === expected ? null : `El dígito de verificación de este NIT es ${expected}`;
};

const IDENTITY_MESSAGES = {
  260: 'El número de documento no es válido. Revisa que tenga solo números y, si es NIT, el dígito de verificación correcto.',
  261: 'Este documento ya está registrado en otra cuenta de Mi Paquete. Si es tuyo, comunícate con soporte.',
  262: 'Esta cuenta bancaria ya está registrada en otra cuenta de Mi Paquete. Si es tuya, comunícate con soporte.',
};

/**
 * Mensaje para el usuario cuando ms_users rechaza el documento o la cuenta bancaria
 * (códigos 260, 261 y 262). Acepta el error de axios o su response.
 * @returns {string|null}
 */
export const identityErrorMessage = (error) => {
  const data = error?.response?.data ?? error?.data;
  const code = data?.message?.code;
  if (IDENTITY_MESSAGES[code]) return IDENTITY_MESSAGES[code];

  const detail = String(data?.message?.detail ?? '');
  if (/documento ya está registrado/i.test(detail)) return IDENTITY_MESSAGES[261];
  if (/cuenta bancaria ya está registrada/i.test(detail)) return IDENTITY_MESSAGES[262];
  if (/^Documento inválido/i.test(detail)) return IDENTITY_MESSAGES[260];
  return null;
};
